<template>
  <div class="ehc-guest-qa-builder">
    <p class="text-body-2 mb-4">
      Ask a question on the guest guidebook searches the full guide, including
      this Q&amp;A page even when the page is hidden. Use this hub for typical
      and actual questions that are not already answered elsewhere.
    </p>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">
      {{ error }}
    </v-alert>

    <v-alert v-if="saveSuccess" type="success" dense outlined class="mb-4">
      {{ saveSuccess }}
    </v-alert>

    <v-card outlined class="mb-4 pa-3">
      <div class="text-subtitle-1 font-weight-medium mb-2">
        Unanswered questions
      </div>
      <p class="text-caption text--secondary mb-3">
        Guests asked these in Ask a question, but the guidebook did not have an
        answer. Add answers below or dismiss when handled.
      </p>
      <template v-if="unansweredQuestions.length">
        <v-card
          v-for="entry in unansweredQuestions"
          :key="entry.id"
          outlined
          class="mb-2 pa-3"
        >
          <div class="text-caption text--secondary mb-1">
            {{ formatAskedAt(entry.askedAt) }}
          </div>
          <div class="text-body-2 font-weight-medium mb-2">
            {{ entry.question }}
          </div>
          <div v-if="entry.aiResponse" class="text-caption text--secondary mb-2">
            Guest was told: {{ entry.aiResponse }}
          </div>
          <div class="d-flex flex-wrap">
            <ehc-btn
              small
              color="primary"
              dark
              class="mr-2 mb-1"
              @click="addUnansweredToBuilder(entry)"
            >
              Add to Q&amp;A
            </ehc-btn>
            <ehc-btn
              small
              outlined
              class="mb-1"
              :loading="dismissingId === entry.id"
              @click="dismissUnanswered(entry.id)"
            >
              Dismiss
            </ehc-btn>
          </div>
        </v-card>
      </template>
      <p v-else class="text-body-2 text--secondary mb-0">
        No unanswered questions. Guests will land here when Ask a question
        cannot find an answer.
      </p>
    </v-card>

    <v-card outlined class="mb-4 pa-3">
      <div class="text-subtitle-1 font-weight-medium mb-2">Q&amp;A page</div>
      <p class="text-caption text--secondary mb-3">
        These question-and-answer pairs become the guest Q&amp;A page. Hidden
        pages stay in Ask a question answers.
      </p>
      <v-switch
        v-model="visibleToGuests"
        label="Show Q&amp;A page to guests"
        color="success"
        dense
        hide-details
        class="mt-0 mb-3"
        :loading="visibilityLoading"
        :disabled="visibilityLoading"
      ></v-switch>

      <div class="d-flex flex-wrap align-center mb-4">
        <ehc-btn
          color="primary"
          dark
          :loading="loadingSuggest"
          :disabled="loadingSave"
          @click="loadSuggestions"
          class="mr-2 mb-2"
        >
          <v-icon left small>mdi-creation</v-icon>
          {{ items.length ? "Regenerate suggestions" : "Generate suggestions" }}
        </ehc-btn>
        <ehc-btn
          outlined
          :disabled="loadingSuggest || loadingSave"
          @click="addBlankItem"
          class="mr-2 mb-2"
        >
          <v-icon left small>mdi-plus</v-icon>
          Add question
        </ehc-btn>
        <span v-if="items.length" class="text-caption mb-2">
          {{ selectedCount }} of {{ items.length }} selected
        </span>
      </div>

      <v-progress-linear
        v-if="loadingSuggest"
        indeterminate
        color="primary"
        class="mb-4"
      ></v-progress-linear>

      <div
        v-if="!items.length && !loadingSuggest"
        class="text-center text--secondary py-6"
      >
        Click <strong>Generate suggestions</strong> to get started, or add a
        question from the unanswered list.
      </div>

      <v-card
        v-for="(item, index) in items"
        :key="item.id"
        outlined
        class="mb-3 pa-3"
      >
        <div class="d-flex align-start">
          <v-checkbox
            v-model="item.selected"
            hide-details
            class="mt-0 pt-0 mr-2"
          ></v-checkbox>
          <div class="flex-grow-1">
            <v-text-field
              v-model="item.question"
              label="Question"
              outlined
              dense
              hide-details="auto"
              class="mb-2"
            ></v-text-field>
            <v-textarea
              v-model="item.answer"
              label="Answer"
              outlined
              dense
              auto-grow
              rows="2"
              :hint="item.hint || 'Write the answer guests should see.'"
              persistent-hint
              class="mb-1"
            ></v-textarea>
            <div v-if="item.reason" class="text-caption text--secondary">
              {{ item.reason }}
              <span v-if="item.category"> · {{ item.category }}</span>
            </div>
          </div>
          <v-btn icon small class="ml-1" @click="removeItem(index)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card>

      <div v-if="items.length" class="d-flex justify-end mt-2">
        <ehc-btn
          color="success"
          dark
          :loading="loadingSave"
          :disabled="loadingSuggest || selectedCount === 0"
          @click="saveSelected"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Save Q&amp;A page
        </ehc-btn>
      </div>
    </v-card>

    <v-card outlined class="mb-4 pa-3">
      <div class="text-subtitle-1 font-weight-medium mb-2">
        Unanswered question emails
      </div>
      <p class="text-caption text--secondary mb-3">
        Email when Ask a question cannot answer a guest.
        {{ orgDefaultTimingHint }}
      </p>
      <v-text-field
        v-model="guestAskNotificationEmail"
        label="Notify unanswered questions to"
        type="email"
        outlined
        dense
        hide-details="auto"
        hint="Required. Use the inbox that should receive unanswered guest questions."
        persistent-hint
        class="mb-2"
        :rules="emailRules"
      ></v-text-field>
      <v-radio-group
        v-model="guestAskNotificationTiming"
        class="mt-0 mb-2"
        hide-details
      >
        <v-radio
          v-for="option in guestAskNotificationTimingOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        ></v-radio>
      </v-radio-group>
      <ehc-btn
        small
        color="primary"
        dark
        :loading="savingNotificationSettings"
        :disabled="sendingQueuedNotifications || !notificationEmailIsValid"
        @click="saveGuestAskNotificationSettings"
      >
        Save notification settings
      </ehc-btn>
      <v-tooltip bottom max-width="280">
        <template #activator="{ on, attrs }">
          <span class="d-inline-block ml-2" v-bind="attrs" v-on="on">
            <ehc-btn
              small
              outlined
              :loading="sendingQueuedNotifications"
              :disabled="savingNotificationSettings"
              @click="sendQueuedNotificationsNow"
            >
              <v-icon left small>mdi-email-fast-outline</v-icon>
              Send batched now
              <span v-if="queuedNotificationCount">
                ({{ queuedNotificationCount }})
              </span>
            </ehc-btn>
          </span>
        </template>
        <span>
          <template v-if="queuedNotificationCount">
            {{ queuedNotificationCount }} unanswered question{{
              queuedNotificationCount === 1 ? "" : "s"
            }}
            waiting for the next-morning email. Click to send them right now.
          </template>
          <template v-else>
            Sends any questions waiting for the next-morning email right now.
          </template>
        </span>
      </v-tooltip>
      <v-alert
        v-if="notificationResult"
        :type="notificationResult.type"
        dense
        outlined
        class="mt-3 mb-0"
      >
        {{ notificationResult.text }}
      </v-alert>
    </v-card>
  </div>
</template>

<script>
import firebase from "firebase";

let nextItemId = 1;

export default {
  name: "EhcGuestQaBuilder",
  props: {
    propertyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      items: [],
      unansweredQuestions: [],
      guestAskNotificationEmail: "",
      guestAskNotificationTiming: "immediate",
      guestAskNotificationTimingOptions: [
        {
          label:
            "Immediately — when a guest asks something Ask a question cannot answer",
          value: "immediate",
        },
        {
          label: "Next morning — one email early the next day (batched)",
          value: "next_morning",
        },
      ],
      emailRules: [
        (value) =>
          (!!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) ||
          "Enter a valid email address",
      ],
      visibleToGuests: true,
      visibilityLoading: false,
      visibilityReady: false,
      dismissingId: null,
      loadingSuggest: false,
      loadingSave: false,
      savingNotificationSettings: false,
      sendingQueuedNotifications: false,
      notificationResult: null,
      error: null,
      saveSuccess: null,
    };
  },
  computed: {
    selectedCount() {
      return this.items.filter((item) => item.selected).length;
    },
    queuedNotificationCount() {
      return this.unansweredQuestions.filter(
        (entry) => entry.notificationStatus === "queued",
      ).length;
    },
    notificationEmailIsValid() {
      const email = String(this.guestAskNotificationEmail || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    orgDefaultTimingHint() {
      const timing = this.gcvOrg?.guestAiNotifications?.timing;
      const label =
        timing === "next_morning" ? "next morning" : "immediately";
      return `Organization default is ${label}.`;
    },
  },
  watch: {
    visibleToGuests() {
      if (!this.visibilityReady || this.visibilityLoading) {
        return;
      }
      this.saveGuestQAVisibility();
    },
  },
  created() {
    this.loadUnansweredQuestions();
    this.loadExistingGuestQAItems();
    this.loadGuestQAVisibility();
  },
  methods: {
    emitUnansweredCount() {
      this.$emit("unansweredCountChange", {
        propertyId: String(this.propertyId),
        count: this.unansweredQuestions.length,
      });
    },
    stripHtmlTags(html) {
      return (html || "")
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    parseGuestQAHtml(html) {
      if (!html || typeof html !== "string") {
        return [];
      }

      const items = [];
      const pattern = /<h3\b[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3\b|$)/gi;
      let match = pattern.exec(html);

      while (match) {
        const question = this.stripHtmlTags(match[1]);
        const answer = (match[2] || "").trim();
        if (question) {
          items.push({ question, answer });
        }
        match = pattern.exec(html);
      }

      return items;
    },
    subsectionsRef() {
      return firebase
        .firestore()
        .collection("properties")
        .doc(String(this.propertyId))
        .collection("propertyinfo")
        .doc("subsections");
    },
    async loadGuestQAVisibility() {
      try {
        const snap = await this.subsectionsRef().get();
        const data = snap.exists ? snap.data() || {} : {};
        const section =
          data.guestQA ||
          Object.values(data).find((entry) => entry?.docId === "guestQA");
        this.visibleToGuests = !section?.hiddenAt;
      } catch (error) {
        console.error("loadGuestQAVisibility failed", error);
      } finally {
        this.$nextTick(() => {
          this.visibilityReady = true;
        });
      }
    },
    async saveGuestQAVisibility() {
      this.visibilityLoading = true;
      this.error = null;
      try {
        const snap = await this.subsectionsRef().get();
        const data = snap.exists ? { ...(snap.data() || {}) } : {};
        const key =
          Object.keys(data).find(
            (entryKey) =>
              entryKey === "guestQA" || data[entryKey]?.docId === "guestQA",
          ) || "guestQA";
        data[key] = {
          ...(data[key] || {}),
          docId: "guestQA",
          label: data[key]?.label || "Guest Q&A",
          pageTitle: data[key]?.pageTitle || "Guest Q&A",
          hiddenAt: this.visibleToGuests ? null : Date.now(),
        };
        await this.subsectionsRef().set(data, { merge: true });
        this.saveSuccess = this.visibleToGuests
          ? "Q&A page is visible to guests."
          : "Q&A page is hidden from guests. Ask a question still uses these answers.";
      } catch (error) {
        console.error("saveGuestQAVisibility failed", error);
        this.error = "Could not update Q&A page visibility. Please try again.";
        this.visibleToGuests = !this.visibleToGuests;
      } finally {
        this.visibilityLoading = false;
      }
    },
    async loadExistingGuestQAItems() {
      try {
        const doc = await firebase
          .firestore()
          .collection("properties")
          .doc(String(this.propertyId))
          .get();
        const existing = this.parseGuestQAHtml(doc.data()?.guestQA);
        if (!existing.length) {
          return;
        }

        const seen = new Set(
          this.items.map((item) =>
            (item.question || "").trim().toLowerCase(),
          ),
        );

        existing.forEach((entry) => {
          const key = entry.question.trim().toLowerCase();
          if (seen.has(key)) {
            return;
          }
          seen.add(key);
          this.items.push(
            this.makeItem({
              question: entry.question,
              suggestedAnswerHint: entry.answer,
            }),
          );
        });
      } catch (error) {
        console.error("loadExistingGuestQAItems failed", error);
      }
    },
    formatAskedAt(askedAt) {
      if (!askedAt) {
        return "";
      }
      return new Date(askedAt).toLocaleString();
    },
    async loadUnansweredQuestions() {
      try {
        const doc = await firebase
          .firestore()
          .collection("properties")
          .doc(String(this.propertyId))
          .get();
        const data = doc.data() || {};
        this.guestAskNotificationEmail = String(
          data.guestAskNotificationEmail || "",
        ).trim();
        const propertyTiming = data.guestAskNotificationTiming;
        if (
          propertyTiming === "next_morning" ||
          propertyTiming === "immediate"
        ) {
          this.guestAskNotificationTiming = propertyTiming;
        } else {
          this.guestAskNotificationTiming =
            this.gcvOrg?.guestAiNotifications?.timing === "next_morning"
              ? "next_morning"
              : "immediate";
        }
        const entries = data.unansweredGuestQuestions || {};
        this.unansweredQuestions = Object.entries(entries)
          .filter(([, entry]) => entry?.status === "open")
          .map(([id, entry]) => ({ id, ...entry }))
          .sort((a, b) => Number(b.askedAt || 0) - Number(a.askedAt || 0));
        this.emitUnansweredCount();
      } catch (error) {
        console.error("loadUnansweredQuestions failed", error);
      }
    },
    async saveGuestAskNotificationSettings() {
      if (!this.notificationEmailIsValid) {
        this.error = "Enter a valid email address for unanswered questions.";
        return;
      }
      this.savingNotificationSettings = true;
      this.error = null;
      this.saveSuccess = null;
      const email = String(this.guestAskNotificationEmail || "")
        .trim()
        .toLowerCase();
      const timing =
        this.guestAskNotificationTiming === "next_morning"
          ? "next_morning"
          : "immediate";

      try {
        await firebase
          .firestore()
          .collection("properties")
          .doc(String(this.propertyId))
          .update({
            guestAskNotificationEmail: email,
            guestAskNotificationTiming: timing,
          });
        this.guestAskNotificationEmail = email;
        this.guestAskNotificationTiming = timing;
        const timingPart =
          timing === "next_morning"
            ? "and send as a next-morning digest"
            : "and send immediately";
        this.saveSuccess = `Notifications will go to ${email} ${timingPart}.`;
      } catch (error) {
        console.error("saveGuestAskNotificationSettings failed", error);
        this.error =
          "Could not save the notification settings. Please try again.";
      } finally {
        this.savingNotificationSettings = false;
      }
    },
    async sendQueuedNotificationsNow() {
      this.sendingQueuedNotifications = true;
      this.error = null;
      this.saveSuccess = null;
      this.notificationResult = {
        type: "info",
        text: "Sending batched unanswered question emails...",
      };

      try {
        const callable = firebase
          .functions()
          .httpsCallable("sendUnansweredGuestQADigestNow");
        const response = await callable({
          propertyId: String(this.propertyId),
        });
        const data = response?.data || {};
        const count = Number(data.questionCount || 0);
        let text;
        let type = "success";
        if (!data.sent || count === 0) {
          type = "info";
          text =
            "Nothing to send — every open guest question has already been emailed.";
        } else {
          const to = data.recipient ? ` to ${data.recipient}` : "";
          text = `Sent ${count} unanswered question email${
            count === 1 ? "" : "s"
          }${to}.`;
        }
        this.notificationResult = { type, text };
        this.$store.commit("setAlert", { type, text });
        await this.loadUnansweredQuestions();
      } catch (error) {
        console.error("sendQueuedNotificationsNow failed", error);
        const text = this.getCallableErrorMessage(error);
        this.notificationResult = { type: "error", text };
        this.$store.commit("setAlert", { type: "error", text });
      } finally {
        this.sendingQueuedNotifications = false;
      }
    },
    async addUnansweredToBuilder(entry) {
      await this.loadExistingGuestQAItems();
      const exists = this.items.some(
        (item) =>
          (item.question || "").trim().toLowerCase() ===
          (entry.question || "").trim().toLowerCase(),
      );
      if (!exists) {
        this.items.push(
          this.makeItem({
            question: entry.question,
            suggestedAnswerHint: "",
            reason: "Asked by a guest via Ask a question",
          }),
        );
      }
    },
    async dismissUnanswered(entryId) {
      this.dismissingId = entryId;
      try {
        await firebase
          .firestore()
          .collection("properties")
          .doc(String(this.propertyId))
          .update({
            [`unansweredGuestQuestions.${entryId}`]:
              firebase.firestore.FieldValue.delete(),
          });
        this.unansweredQuestions = this.unansweredQuestions.filter(
          (entry) => entry.id !== entryId,
        );
        this.emitUnansweredCount();
      } catch (error) {
        console.error("dismissUnanswered failed", error);
        this.error = "Could not dismiss that question. Please try again.";
      } finally {
        this.dismissingId = null;
      }
    },
    async dismissMatchingUnanswered(savedItems) {
      const savedQuestions = new Set(
        savedItems.map((item) => item.question.trim().toLowerCase()),
      );
      const matches = this.unansweredQuestions.filter((entry) =>
        savedQuestions.has((entry.question || "").trim().toLowerCase()),
      );
      for (const entry of matches) {
        await this.dismissUnanswered(entry.id);
      }
    },
    makeItem(suggestion = {}) {
      return {
        id: nextItemId++,
        selected: true,
        question: suggestion.question || "",
        answer: suggestion.suggestedAnswerHint || "",
        reason: suggestion.reason || "",
        category: suggestion.category || "",
        hint: suggestion.suggestedAnswerHint || "",
      };
    },
    addBlankItem() {
      this.items.push(this.makeItem());
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    getCallableErrorMessage(error) {
      const code = error?.code ? String(error.code) : "";
      const message =
        error?.message ||
        error?.details ||
        "Something went wrong. Please try again.";

      if (code === "functions/permission-denied") {
        return "Guest Q&A is available for Pro, Premium, and Guest Ask properties only.";
      }
      if (code === "functions/failed-precondition") {
        return message;
      }
      if (code === "functions/resource-exhausted") {
        return "Too many requests. Please wait a few minutes and try again.";
      }
      if (code === "functions/internal" && /memory/i.test(message)) {
        return "The server ran out of memory. Try again in a moment.";
      }
      if (code === "functions/internal") {
        return message === "internal"
          ? "The AI service failed. Confirm the functions are deployed and try again."
          : message;
      }
      return message;
    },
    async loadSuggestions() {
      this.loadingSuggest = true;
      this.error = null;
      this.saveSuccess = null;

      try {
        const callable = firebase.functions().httpsCallable("suggestPropertyQA");
        const response = await callable({
          propertyId: String(this.propertyId),
        });
        const suggestions = response?.data?.suggestions || [];

        if (!suggestions.length) {
          this.error =
            "No suggestions were returned. Add more guide content and try again.";
          return;
        }

        await this.loadExistingGuestQAItems();
        const seen = new Set(
          this.items.map((item) =>
            (item.question || "").trim().toLowerCase(),
          ),
        );
        suggestions.forEach((suggestion) => {
          const key = (suggestion.question || "").trim().toLowerCase();
          if (!key || seen.has(key)) {
            return;
          }
          seen.add(key);
          this.items.push(this.makeItem(suggestion));
        });
      } catch (error) {
        console.error("loadSuggestions failed", error);
        this.error = this.getCallableErrorMessage(error);
      } finally {
        this.loadingSuggest = false;
      }
    },
    async saveSelected() {
      const payloadItems = this.items
        .filter((item) => item.selected)
        .map((item) => ({
          question: (item.question || "").trim(),
          answer: (item.answer || "").trim(),
        }))
        .filter((item) => item.question);

      if (!payloadItems.length) {
        this.error = "Select at least one question with text to save.";
        return;
      }

      this.loadingSave = true;
      this.error = null;
      this.saveSuccess = null;

      try {
        const callable = firebase.functions().httpsCallable("savePropertyQAPage");
        const response = await callable({
          propertyId: String(this.propertyId),
          items: payloadItems,
        });
        const data = response?.data || {};
        const action = data.created ? "created" : "updated";
        this.saveSuccess = `Q&A page ${action} with ${data.itemCount} question(s).`;
        this.$store.commit("setAlert", {
          type: "success",
          text: this.saveSuccess,
        });
        await this.dismissMatchingUnanswered(payloadItems);
        await this.loadUnansweredQuestions();
        this.$emit("saved", data);
      } catch (error) {
        console.error("saveSelected failed", error);
        this.error = this.getCallableErrorMessage(error);
        this.$store.commit("setAlert", {
          type: "error",
          text: `Failed to save Guest Q&A: ${this.getCallableErrorMessage(error)}`,
        });
      } finally {
        this.loadingSave = false;
      }
    },
  },
};
</script>

<style scoped>
.ehc-guest-qa-builder {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 4px;
}
</style>
